import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';
import { useOAuthAPI } from '../../../api/use-oauth-api';
import { getSwrFetcherWithDynamicUrl } from '../../../utils/get-swr-fetcher-with-dynamic-url';
import { OAuthConnectionInformation } from '../../integrations/types/types';
import {
  DisconnectGmailResponse,
  GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_KEY,
  INVOICE_SCANNING_CARD_HIDDEN_KEY,
} from '../invoice-pay-page-inner-wrapper';

const DISCONNECT_EMAIL_URL = '/api/oauth/disconnect?provider=gmail';

export const useGmailConnection = () => {
  const { checkEmailConnection } = useOAuthAPI();

  const [isCheckingGmailConnection, setIsCheckingGmailConnection] = useState(true);
  const [gmailConnectionStatus, setGmailConnectionStatus] = useState<OAuthConnectionInformation | null>(null);
  const { trigger: triggerDisconnectGmail, isMutating: isDisconnectingGmail } = useSWRMutation<
    DisconnectGmailResponse,
    AxiosError,
    string,
    { urlSuffix: string }
  >(DISCONNECT_EMAIL_URL, getSwrFetcherWithDynamicUrl);

  // TODO: can this be moved to SWR?
  useEffect(() => {
    checkGmailConnectionStatus();
  }, []);

  const checkGmailConnectionStatus = async () => {
    setIsCheckingGmailConnection(true);

    try {
      const status = await checkEmailConnection();

      setGmailConnectionStatus(status);
    } catch (error) {
      setGmailConnectionStatus({
        connected: false,
        error: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsCheckingGmailConnection(false);
    }
  };

  const onDisconnectGmail = async (connectionId: string) => {
    try {
      const result = await triggerDisconnectGmail({ urlSuffix: `&connectionId=${connectionId}` });

      if (result.success) {
        await checkGmailConnectionStatus();
      }

      // TODO: remove sessionStorage.removeItem in some time, once the users that were setting this state in sessionStorage are no longer using it
      sessionStorage.removeItem(GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_KEY);
      sessionStorage.removeItem(INVOICE_SCANNING_CARD_HIDDEN_KEY);
      localStorage.removeItem(GMAIL_EXTRACTION_AFTER_CONNECTION_COMPLETED_KEY);
      localStorage.removeItem(INVOICE_SCANNING_CARD_HIDDEN_KEY);

      toast.success('Gmail disconnected successfully');
    } catch (error) {
      Sentry.captureException(error);

      toast.error('Failed to disconnect Gmail');
    }
  };

  return {
    gmailConnectionStatus,
    isCheckingGmailConnection,
    isDisconnectingGmail,
    mutations: {
      onDisconnectGmail,
    },
  };
};
